import { Box, Container, Grid, Typography, useMediaQuery, useTheme } from '@ace-clublabs/shared-component-library';
import { MembershipDetails, MembershipCTAs, Notifications } from '../components';
import { MembershipBreadcrumbs } from '../components/Breadcrumbs/Breadcrumbs';
import { MembershipOverview } from '../components/MembershipOverview';
import { PaymentCard } from '../components/PaymentCard/PaymentCard';
import { MembershipIWTs } from '../components/MembershipButtonGroups';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { setGlobalState, useGlobalState } from '../state/state';
import { generateInitialFormData, getIsMembershipChanging } from '../state/helpers';
import { buildNotifications } from '../components/Notifications/buildNotifications';
import { HomePageDisclaimer } from '../components/Disclaimers/HomePageDisclaimer';
import { RestrictedHomeView } from '../components/RestrictedHomeView/RestrictedHomeView';
import { UtagHelper, UtagType } from '../utils/UtagHelper';
import { DownloadAppBlade } from '../components/DownloadAppBlade/DownloadAppBlade';
import { MembershipWallet } from '../components/MembershipWallet';
import { RestrictedNotification } from '../components/RestrictedHomeView/RestrictedNotification';

export const MembershipHome: React.FC = () => {
  let history = useHistory();
  const [memberDetails] = useGlobalState('memberDetails');
  const [formData] = useGlobalState('formData');
  const [paymentInfo] = useGlobalState('paymentInfo');
  const [isMobileApp] = useGlobalState('isMobileApp');
  const [tags] = useGlobalState('tags');
  const canChangeMembership = memberDetails?.loggedInMember?.rules?.changeMembership;
  const [isTagging, setTagging] = useState(true);

  const theme = useTheme();
  const isMobileScreenSize = useMediaQuery(theme.breakpoints.down('md'));

  const boxRef = useRef<HTMLElement>(null);
  const [boxHeight, setBoxHeight] = useState('340px');
  const updateBoxHeight = () => {
    if (boxRef.current) {
      const height = boxRef.current.offsetHeight; // Measure the height of the box container
      setBoxHeight(`${height + 40}px`); // Set the box background height to the size of the container
    }
  };

  //This is to initialise the box height when page isfirst rendered
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;

    updateBoxHeight();

    const resizeObserver = new ResizeObserver(updateBoxHeight);
    resizeObserver.observe(el);

    return () => resizeObserver.disconnect();
  }, [isTagging]);

  
  useEffect(() => {
    updateBoxHeight();

    window.addEventListener('resize', updateBoxHeight);

    return () => {
      window.removeEventListener('resize', updateBoxHeight);
    };
  }, []);

  useEffect(() => {
    const hasMembershipChanged = getIsMembershipChanging(memberDetails, formData);
    if (hasMembershipChanged) {
      const newFormData = generateInitialFormData(memberDetails);
      setGlobalState('formData', newFormData);
    }
    setGlobalState('notifications', buildNotifications(memberDetails, paymentInfo, tags, history));
  }, []);

  useLayoutEffect(() => {
    UtagHelper.sendTag(UtagType.view, tags, '');
    setTagging(false);
  }, []);

  if (isTagging) {
    return <></>;
  }

  return (
    <Container maxWidth="lg">
      {/*remember to update both mobile and desktop for code change*/}
      {isMobileScreenSize ? (
        <>
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              zIndex: -1,
              width: '100vw',
              height: boxHeight,
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: '#F7F5F1',
              alignItems: 'center',
            }}
          />
          <Grid container columnSpacing={'1.5rem'} sx={{ marginBottom: '5rem' }}>
            <Grid size={12}>
              <Box ref={boxRef}>
                {!isMobileApp && <MembershipBreadcrumbs />}
                <MembershipDetails />
                {!isMobileApp && <MembershipWallet />}
                {canChangeMembership && <MembershipCTAs />}
              </Box>
              {canChangeMembership ? (
                <Grid size={12}>
                  <Notifications />
                  <Typography variant="h5">Payment details</Typography>
                  <PaymentCard />
                  <MembershipIWTs />
                </Grid>
              ) : (
                <Grid size={12} sx={{ marginTop: '6.2rem' }}>
                  <RestrictedNotification />
                </Grid>
              )}
              <MembershipOverview />
              <DownloadAppBlade />
              <HomePageDisclaimer />
              {/* <FAQ /> */}
            </Grid>
          </Grid>
        </>
      ) : (
        <>
          <Grid container columnSpacing={'1.5rem'} sx={{ marginBottom: '5rem' }}>
            <Grid size={7}>
              <Box ref={boxRef}>
                {!isMobileApp && <MembershipBreadcrumbs />}
                <MembershipDetails />
                {canChangeMembership && <MembershipCTAs />}
              </Box>
              {canChangeMembership && <Notifications />}
              <MembershipOverview />
              <HomePageDisclaimer />
              {/* <FAQ /> */}
            </Grid>
            {canChangeMembership ? (
              <Grid size={5}>
                <PaymentCard />
                <MembershipIWTs />
                <DownloadAppBlade />
              </Grid>
            ) : (
              <Grid size={5} sx={{ marginTop: '6.2rem' }}>
                <RestrictedHomeView />
              </Grid>
            )}
          </Grid>
        </>
      )}
    </Container>
  );
};
